/**
 * The globe's MapLibre style, built entirely from local data.
 *
 * INVARIANT 7 — no labels, ever. Structurally guaranteed here:
 *   - no `glyphs` and no `sprite` on the style, so text and icons cannot render
 *     even if a symbol layer were somehow added later;
 *   - no `symbol` layer;
 *   - every source is inline `geojson` from `@geogame/data`, so there is no
 *     tile endpoint that could serve a label layer and no network request at
 *     all. `style.test.ts` asserts all three.
 *
 * MICROSTATES — Natural Earth 110m has no polygon for 30 countries (Singapore,
 * Monaco, Vatican, Nauru, Malta, Andorra …). `microstateDots()` gives them a
 * point source drawn as tiny land-coloured islands. Picking them is handled by
 * projection + distance in the component rather than by a hit layer, so it is
 * exact rather than dependent on how MapLibre hit-tests circles.
 */

import type { Feature, FeatureCollection, Geometry, LineString, Point } from 'geojson';
import type { LayerSpecification, StyleSpecification } from 'maplibre-gl';
import { allCountries, featureFor, geojson, microShapes } from '@geogame/data';

import { buildGraticule, lngLatOf } from './geo';
import { emptyMarkCollection } from './marks';
import { TWILIGHT_BANDS, emptyNightBands } from './terminator';
import { COLORS, SNAP } from './theme';

/*
 * MapLibre re-exports only the `LayerSpecification` union and
 * `StyleSpecification`, not the individual property-value spec types. Deriving
 * them from the union keeps every expression below fully type-checked without
 * reaching into `@maplibre/maplibre-gl-style-spec` (not a dependency here) and
 * without a single `any`.
 */
type FillLayer = Extract<LayerSpecification, { type: 'fill' }>;
type FillPaint = NonNullable<FillLayer['paint']>;
type ColourValue = NonNullable<FillPaint['fill-color']>;
type NumberValue = NonNullable<FillPaint['fill-opacity']>;
type CircleLayer = Extract<LayerSpecification, { type: 'circle' }>;
type FilterValue = NonNullable<CircleLayer['filter']>;
type SkySpec = NonNullable<StyleSpecification['sky']>;
type SkyNumber = NonNullable<SkySpec['atmosphere-blend']>;

export const SOURCE = {
  countries: 'geogame-countries',
  micro: 'geogame-micro',
  /** Draw-only outlines for the countries 110m has no polygon for. */
  microShape: 'geogame-microshape',
  graticule: 'geogame-graticule',
  /** First-order subdivisions. Empty until the subdivision mode fills it. */
  regions: 'geogame-regions',
  pins: 'geogame-pins',
  marks: 'geogame-marks',
  night: 'geogame-night',
  /** A world-covering polygon, drawn transparent until an eclipse falls. */
  eclipse: 'geogame-eclipse',
} as const;

export const LAYER = {
  ocean: 'geogame-ocean',
  /** Four nested twilight caps. See `terminator.ts`. */
  night: 'geogame-night',
  /** The eclipse shadow. Always present, transparent until it is not. */
  eclipse: 'geogame-eclipse',
  graticule: 'geogame-graticule',
  countriesFill: 'geogame-countries-fill',
  countriesLine: 'geogame-countries-line',
  microShapeFill: 'geogame-microshape-fill',
  microShapeLine: 'geogame-microshape-line',
  microDot: 'geogame-micro-dot',
  regionsFill: 'geogame-regions-fill',
  regionsLine: 'geogame-regions-line',
  markPulse: 'geogame-mark-pulse',
  markRing: 'geogame-mark-ring',
  markCore: 'geogame-mark-core',
  pinHalo: 'geogame-pin-halo',
  pinTargetRing: 'geogame-pin-target-ring',
  pinChoiceRing: 'geogame-pin-choice-ring',
  pinOpponentRing: 'geogame-pin-opponent-ring',
  pinCore: 'geogame-pin-core',
} as const;

/**
 * The only layers `queryRenderedFeatures` is allowed to see when resolving a
 * tap. Microstates are NOT picked by query — they have no polygon, so the
 * component projects their dot positions and measures screen distance
 * directly, which is exact and does not depend on circle-query semantics.
 */
/*
 * `microShapeFill` is deliberately NOT in here. Those outlines are drawn from a
 * 50m dataset the rest of the game knows nothing about; letting a tap resolve
 * against them would mean two different geometries deciding what the player
 * clicked, and the one that decides has to be the one `snap.ts`, `targets.ts`
 * and `selectability.test.ts` were measured against.
 */
export const POLYGON_PICK_LAYERS: string[] = [LAYER.countriesFill];

/**
 * What a tap resolves against while the SUBDIVISION mode is running.
 *
 * A separate list rather than an addition to the one above: with both in play a
 * tap on Ohio would resolve to `USA` or to `US-OH` depending on which layer
 * MapLibre returned first, and the round is graded against exactly one of
 * those. The globe swaps the list wholesale when `regions` arrives, so at any
 * moment there is one geometry deciding what the player clicked — the same rule
 * `microShapeFill` is kept out of the country list for.
 */
export const REGION_PICK_LAYERS: string[] = [LAYER.regionsFill];

/** Feature-state key carrying the highlight kind. */
export const HL_KEY = 'hl';

/**
 * `choice` is a rung-2 map-choice dot — one of the four cities the subdivision
 * mode asks the player to choose between, and the only pin kind that is a
 * QUESTION rather than an answer. Drawn in the neutral gold the game uses for
 * "look here", never in the correct/incorrect pair, because at the moment it is
 * on screen nobody knows which it is.
 */
export type PinKind = 'you' | 'opponent' | 'target' | 'choice';

/**
 * Paint properties the pulse animation writes every frame. Named here so the
 * component and its tests agree on them without a string literal in two files.
 */
export const MARK_PULSE_RADIUS_PROP = 'circle-radius';
export const MARK_PULSE_OPACITY_PROP = 'circle-opacity';

export interface PinProps {
  readonly pid: string;
  readonly kind: PinKind;
}

/** One subdivision polygon. `code` is ISO 3166-2 — `US-OH`, not `USA`. */
export interface RegionProps {
  readonly code: string;
  readonly name: string;
}

export type RegionCollection = FeatureCollection<Geometry, RegionProps>;

export function emptyRegionCollection(): RegionCollection {
  return { type: 'FeatureCollection', features: [] };
}

export type PinCollection = FeatureCollection<Point, PinProps>;

export interface MicrostateDot {
  readonly cca3: string;
  readonly name: string;
  readonly lngLat: readonly [number, number];
  /**
   * Extent of this country's largest island, in degrees, or 0 when it has no
   * drawable shape at all. See `DOT_RETIRE_DEG`.
   */
  readonly extDeg: number;
}

let microCache: readonly MicrostateDot[] | null = null;

/**
 * Countries the polygon set cannot hit-test: 110m has no geometry for them at
 * all. Without this dot layer they are unaskable (spec section 6).
 */
export function microstateDots(): readonly MicrostateDot[] {
  if (microCache) return microCache;
  const ext = new Map(
    microShapes().features.map((f) => [f.properties.cca3, f.properties.extDeg] as const),
  );
  const dots: MicrostateDot[] = [];
  for (const c of allCountries()) {
    if (c.isDisputed) continue;
    if (featureFor(c.cca3)) continue;
    dots.push({
      cca3: c.cca3,
      name: c.name,
      lngLat: lngLatOf(c),
      extDeg: ext.get(c.cca3) ?? 0,
    });
  }
  microCache = dots;
  return microCache;
}

function microCollection(): FeatureCollection<Point, MicroDotProps> {
  return {
    type: 'FeatureCollection',
    features: microstateDots().map(
      (d): Feature<Point, MicroDotProps> => ({
        type: 'Feature',
        properties: { cca3: d.cca3, extDeg: d.extDeg },
        geometry: { type: 'Point', coordinates: [d.lngLat[0], d.lngLat[1]] },
      }),
    ),
  };
}

interface MicroDotProps {
  readonly cca3: string;
  readonly extDeg: number;
}

/**
 * Largest-island extent, in degrees, at or above which the stand-in dot RETIRES
 * as the camera comes in and lets the country's real outline speak for itself.
 *
 * A per-country threshold rather than one zoom for everybody, because these
 * countries are not one size. Malta spans 0.216 degrees and is 20 px across by
 * zoom 6; the Vatican spans 0.011 and is still under two pixels at the camera's
 * maximum. Retiring both on the same schedule either leaves a circle sitting on
 * a perfectly legible Malta or strands the Vatican with nothing to aim at.
 *
 * 0.15 puts the line between Monaco, Nauru, Tuvalu, the Maldives, San Marino
 * and the Vatican - which keep their dot at every zoom - and everything else,
 * which is at least 13 px across by the time the fade completes.
 */
export const DOT_RETIRE_DEG = 0.15;

/** Zoom window over which a retiring dot fades out. */
const DOT_FADE_IN = 4.8;
const DOT_FADE_OUT = 6;

/**
 * Opacity for anything drawn as part of the stand-in dot.
 *
 * ZOOM OUTERMOST, feature data inside the stops: that is a zoom-and-property
 * function, and it is the only nesting MapLibre accepts. An `interpolate` over
 * `["zoom"]` tucked inside a `case` is rejected outright as "zoom expressions
 * may only be used as a top-level function".
 */
export function dotOpacity(full: number): NumberValue {
  return [
    'interpolate',
    ['linear'],
    ['zoom'],
    DOT_FADE_IN,
    full,
    DOT_FADE_OUT,
    ['case', ['>=', ['get', 'extDeg'], DOT_RETIRE_DEG], 0, full],
  ] as unknown as NumberValue;
}

function graticuleCollection(): FeatureCollection<LineString, Record<string, never>> {
  return {
    type: 'FeatureCollection',
    features: buildGraticule().map(
      (line): Feature<LineString, Record<string, never>> => ({
        type: 'Feature',
        properties: {},
        geometry: { type: 'LineString', coordinates: line.map(([lng, lat]) => [lng, lat]) },
      }),
    ),
  };
}

export function emptyPinCollection(): PinCollection {
  return { type: 'FeatureCollection', features: [] };
}

/** Colour driven by the highlight feature-state — never by a layer rebuild. */
function highlightColour(base: string): ColourValue {
  return [
    'case',
    ['==', ['feature-state', HL_KEY], 'correct'],
    COLORS.correct,
    ['==', ['feature-state', HL_KEY], 'incorrect'],
    COLORS.incorrect,
    ['==', ['feature-state', HL_KEY], 'neutral'],
    COLORS.neutral,
    base,
  ];
}

function pinColour(): ColourValue {
  return [
    'match',
    ['get', 'kind'],
    'you',
    COLORS.pinYou,
    'opponent',
    COLORS.pinOpponent,
    'target',
    COLORS.pinTarget,
    'choice',
    COLORS.pinChoice,
    COLORS.pinYou,
  ];
}

/** Mark colour by kind. The same two colours the highlight feature-state uses. */
function markColour(): ColourValue {
  return [
    'match',
    ['get', 'kind'],
    'correct',
    COLORS.correct,
    'incorrect',
    COLORS.incorrect,
    COLORS.neutral,
  ];
}

function pinSize(you: number, opponent: number, target: number, choice: number): NumberValue {
  return [
    'match',
    ['get', 'kind'],
    'you',
    you,
    'opponent',
    opponent,
    'target',
    target,
    'choice',
    choice,
    you,
  ];
}

function kindFilter(kind: PinKind): FilterValue {
  return ['==', ['get', 'kind'], kind];
}

function layers(showGraticule: boolean, showNight: boolean): LayerSpecification[] {
  const list: LayerSpecification[] = [
    {
      id: LAYER.ocean,
      type: 'background',
      paint: { 'background-color': COLORS.ocean },
    },
  ];

  if (showNight) {
    // Four caps, darkest last, each its OWN layer so their alphas compound into
    // a smooth ramp. One layer with a data-driven opacity would draw the
    // overlapping features in a single pass and give four visible steps.
    //
    // They sit here — above the imagery, below the graticule, borders, dots,
    // marks and pins — because night must change how the world LOOKS without
    // changing what the player can find. Everything readable draws on top.
    TWILIGHT_BANDS.forEach((band, i) => {
      list.push({
        id: `${LAYER.night}-${i}`,
        type: 'fill',
        source: SOURCE.night,
        filter: ['==', ['get', 'band'], i],
        paint: {
          'fill-color': COLORS.night,
          'fill-opacity': band.opacity,
          'fill-antialias': false,
        },
      });
    });
  }

  /*
   * The eclipse shadow.
   *
   * Same slot as the twilight caps and for the same reason: it changes how the
   * world LOOKS without covering what the player must find. Pins, marks and
   * the graticule draw on top, so an eclipsed player can still see where they
   * tapped — the handicap is the missing borders, not a blindfold.
   */
  list.push({
    id: LAYER.eclipse,
    type: 'fill',
    source: SOURCE.eclipse,
    paint: {
      'fill-color': COLORS.night,
      'fill-opacity': 0,
      'fill-antialias': false,
    },
  });

  if (showGraticule) {
    list.push({
      id: LAYER.graticule,
      type: 'line',
      source: SOURCE.graticule,
      paint: {
        'line-color': COLORS.graticule,
        'line-width': 0.6,
        'line-opacity': 0.8,
      },
    });
  }

  list.push(
    {
      id: LAYER.countriesFill,
      type: 'fill',
      source: SOURCE.countries,
      paint: {
        'fill-color': highlightColour(COLORS.land),
        'fill-antialias': true,
      },
    },
    {
      id: LAYER.countriesLine,
      type: 'line',
      source: SOURCE.countries,
      paint: {
        'line-color': highlightColour(COLORS.border),
        'line-width': ['interpolate', ['linear'], ['zoom'], 0, 0.5, 4, 1.2],
        'line-opacity': 0.85,
      },
    },
    /*
     * FIRST-ORDER SUBDIVISIONS. Empty and invisible in every mode but one, so
     * the two layers cost a MapLibre source with no features until the client
     * hands the globe a collection.
     *
     * The fill is almost entirely transparent on purpose: the imagery skin
     * below it is what makes the globe worth looking at, and painting fifty
     * opaque states over it would replace a photograph of North America with a
     * flat chart. It has to EXIST, though — `queryRenderedFeatures` only
     * returns a feature the renderer actually drew, so a subdivision with no
     * fill is a subdivision nobody can tap — and the highlight expression paints
     * it properly the moment a round resolves.
     */
    {
      id: LAYER.regionsFill,
      type: 'fill',
      source: SOURCE.regions,
      paint: {
        'fill-color': highlightColour(COLORS.land),
        'fill-opacity': [
          'case',
          ['!=', ['feature-state', HL_KEY], null],
          0.55,
          0.01,
        ] as NumberValue,
        'fill-antialias': true,
      },
    },
    {
      id: LAYER.regionsLine,
      type: 'line',
      source: SOURCE.regions,
      paint: {
        'line-color': COLORS.regionBorder,
        'line-width': ['interpolate', ['linear'], ['zoom'], 1, 0.4, 5, 1.4],
        'line-opacity': 0.8,
      },
    },
    /*
     * The real outline for a country 110m has no polygon for. Draw-only — see
     * `POLYGON_PICK_LAYERS`. It sits above the country fill so it is not
     * covered by a neighbour's polygon, and below the country LINE so the
     * border pair still draws on top of everything.
     */
    {
      id: LAYER.microShapeFill,
      type: 'fill',
      source: SOURCE.microShape,
      paint: {
        'fill-color': highlightColour(COLORS.land),
        'fill-antialias': true,
      },
    },
    {
      id: LAYER.microShapeLine,
      type: 'line',
      source: SOURCE.microShape,
      paint: {
        'line-color': highlightColour(COLORS.border),
        'line-width': ['interpolate', ['linear'], ['zoom'], 0, 0.5, 4, 1.2],
        'line-opacity': 0.85,
      },
    },
    {
      id: LAYER.microDot,
      type: 'circle',
      source: SOURCE.micro,
      paint: {
        'circle-radius': [
          'interpolate',
          ['linear'],
          ['zoom'],
          0,
          SNAP.microDotRadiusPx,
          5,
          SNAP.microDotRadiusPx * 2.4,
        ],
        'circle-color': highlightColour(COLORS.microstate),
        'circle-opacity': dotOpacity(1),
        'circle-stroke-color': COLORS.microstateEdge,
        'circle-stroke-width': 0.8,
        'circle-stroke-opacity': dotOpacity(0.9),
      },
    },
    /*
     * The answer mark, in three layers.
     *
     * `markPulse` is the only thing in this style whose paint the component
     * animates per frame — one `setPaintProperty` against a source holding at
     * most two points, which is cheaper than a single DOM marker reflow. The
     * ring and the core are STATIC so the mark is fully readable the instant it
     * appears and stays readable under `prefers-reduced-motion`, when the pulse
     * never runs at all.
     *
     * `circle-pitch-alignment: viewport` is what makes the ring screen-space:
     * it is the same size on Nauru's 2.6px dot as on Brazil's polygon, which is
     * the whole reason this exists rather than a feature-state colour swap.
     */
    {
      id: LAYER.markPulse,
      type: 'circle',
      source: SOURCE.marks,
      paint: {
        'circle-radius': SNAP.markRingPx,
        'circle-color': markColour(),
        'circle-opacity': 0,
        'circle-pitch-alignment': 'viewport',
      },
    },
    {
      id: LAYER.markRing,
      type: 'circle',
      source: SOURCE.marks,
      paint: {
        'circle-radius': SNAP.markRingPx,
        'circle-color': markColour(),
        'circle-opacity': 0.14,
        'circle-stroke-color': markColour(),
        'circle-stroke-width': 2.6,
        'circle-stroke-opacity': 0.95,
        'circle-pitch-alignment': 'viewport',
      },
    },
    {
      id: LAYER.markCore,
      type: 'circle',
      source: SOURCE.marks,
      paint: {
        'circle-radius': 3.4,
        'circle-color': markColour(),
        'circle-stroke-color': COLORS.pinStroke,
        'circle-stroke-width': 1.2,
        'circle-pitch-alignment': 'viewport',
      },
    },
    {
      id: LAYER.pinHalo,
      type: 'circle',
      source: SOURCE.pins,
      paint: {
        'circle-radius': pinSize(15, 12, 14, 19),
        'circle-color': pinColour(),
        'circle-opacity': 0.18,
      },
    },
    {
      id: LAYER.pinTargetRing,
      type: 'circle',
      source: SOURCE.pins,
      filter: kindFilter('target'),
      paint: {
        'circle-radius': 10,
        'circle-color': COLORS.pinTarget,
        'circle-opacity': 0,
        'circle-stroke-color': COLORS.pinTarget,
        'circle-stroke-width': 2.5,
      },
    },
    /*
     * A map-choice dot has to read as "tap me", not as a label. It is the only
     * pin the player is meant to aim AT, so it gets the widest ring of the
     * four kinds — and a ring rather than a bigger core, because four solid
     * discs at this size merge into one another on a small state.
     */
    {
      id: LAYER.pinChoiceRing,
      type: 'circle',
      source: SOURCE.pins,
      filter: kindFilter('choice'),
      paint: {
        'circle-radius': 11,
        'circle-color': COLORS.pinChoice,
        'circle-opacity': 0,
        'circle-stroke-color': COLORS.pinChoice,
        'circle-stroke-width': 2.2,
      },
    },
    {
      id: LAYER.pinOpponentRing,
      type: 'circle',
      source: SOURCE.pins,
      filter: kindFilter('opponent'),
      paint: {
        'circle-radius': 8,
        'circle-color': COLORS.pinOpponent,
        'circle-opacity': 0,
        'circle-stroke-color': COLORS.pinOpponent,
        'circle-stroke-width': 1.6,
      },
    },
    {
      id: LAYER.pinCore,
      type: 'circle',
      source: SOURCE.pins,
      paint: {
        'circle-radius': pinSize(6.5, 4.5, 3.5, 6),
        'circle-color': pinColour(),
        'circle-stroke-color': COLORS.pinStroke,
        'circle-stroke-width': 1.8,
      },
    },
  );

  return list;
}

function atmosphereBlend(): SkyNumber {
  return ['interpolate', ['linear'], ['zoom'], 0, 0.6, 4, 0];
}

export interface StyleOptions {
  readonly graticule?: boolean;
  /** Draw the real day/night terminator. See `terminator.ts`. */
  readonly night?: boolean;
}

/**
 * The whole style. Self-contained: inline GeoJSON only, no `glyphs`, no
 * `sprite`, no tile URLs, no symbol layers, zero network requests.
 */
export function buildStyle(opts: StyleOptions = {}): StyleSpecification {
  return {
    version: 8,
    name: 'geogame-globe',
    projection: { type: 'globe' },
    sky: {
      'sky-color': COLORS.space,
      'horizon-color': '#16304d',
      'sky-horizon-blend': 0.4,
      'atmosphere-blend': atmosphereBlend(),
    },
    sources: {
      [SOURCE.countries]: {
        type: 'geojson',
        data: geojson(),
        promoteId: 'cca3',
        tolerance: 0.4,
        buffer: 16,
      },
      [SOURCE.micro]: {
        type: 'geojson',
        data: microCollection(),
        promoteId: 'cca3',
      },
      [SOURCE.microShape]: {
        type: 'geojson',
        data: microShapes(),
        promoteId: 'cca3',
      },
      [SOURCE.graticule]: {
        type: 'geojson',
        data: graticuleCollection(),
      },
      // Seeded empty. The subdivision polygons are a quarter of a megabyte and
      // two of the three game modes never draw one, so the client fetches them
      // on demand and pushes them in through the `regions` prop.
      [SOURCE.regions]: {
        type: 'geojson',
        data: emptyRegionCollection(),
        promoteId: 'code',
        tolerance: 0.3,
        buffer: 16,
      },
      // Seeded empty; `Globe.tsx` computes the real caps and refreshes them on
      // a timer, so the style stays a pure function of its options.
      [SOURCE.eclipse]: {
        type: 'geojson',
        /*
         * A polygon covering the whole world, drawn at zero opacity.
         *
         * PRESENT IN EVERY STYLE, because an eclipse arrives mid-match and
         * MapLibre cannot add a layer to a running style without a restyle —
         * and a restyle drops the imagery tiles, which would turn a dramatic
         * moment into a white flash. A transparent fill costs one draw call.
         */
        data: {
          type: 'Feature',
          properties: {},
          geometry: {
            type: 'Polygon',
            coordinates: [
              [
                [-180, -90],
                [180, -90],
                [180, 90],
                [-180, 90],
                [-180, -90],
              ],
            ],
          },
        },
      },
      [SOURCE.night]: {
        type: 'geojson',
        data: emptyNightBands(),
      },
      [SOURCE.pins]: {
        type: 'geojson',
        data: emptyPinCollection(),
        promoteId: 'pid',
      },
      [SOURCE.marks]: {
        type: 'geojson',
        data: emptyMarkCollection(),
        promoteId: 'mid',
      },
    },
    layers: layers(opts.graticule !== false, opts.night === true),
  };
}
